import { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { toast } from "../../Utills/toast";
import { useTranslation } from "react-i18next";
import PasswordInput from "../../component/common/PasswordInput";
import { ButtonLoader, PageLoader } from "../../component/common/Loader";
import { advancedBillingApi } from "../../services/advancedBillingApi";
import { roleMap } from "../../Utills/enums";
import "./SecuritySettings.css";

const defaults = {
  requireManagerPin: true,
  requirePinForRefund: true,
  requirePinForVoid: true,
  requirePinForPaymentCorrection: true,
  enableCounterScreenLock: false,
  counterScreenLockMinutes: 10
};

const SecuritySettings = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(defaults);
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState("");
  const [users, setUsers] = useState([]);
  const [pinUserId, setPinUserId] = useState("");
  const [counterPin, setCounterPin] = useState("");
  const currentRoleId = Number((() => {
    try { return JSON.parse(localStorage.getItem("admin_token_info") || "{}").roleId; }
    catch { return 0; }
  })());
  const manageableUsers = users.filter(user => currentRoleId === 1 || Number(user.roleId) !== 1);

  useEffect(() => {
    Promise.all([advancedBillingApi.getSensitiveActionSettings(), advancedBillingApi.getCounterLockUsers()])
      .then(([value, userList]) => {
        setSettings({ ...defaults, ...(value || {}) });
        setUsers(userList || []);
        const firstManageable = (userList || []).find(user => currentRoleId === 1 || Number(user.roleId) !== 1);
        if (firstManageable) setPinUserId(String(firstManageable.userId));
      })
      .catch(error => toast.error(error.message))
      .finally(() => setLoading(false));
  }, []);

  const setOption = (name, checked) => setSettings(current => ({ ...current, [name]: checked }));
  const saveSettings = async event => {
    event.preventDefault();
    try {
      setSaving("settings");
      const result = await advancedBillingApi.updateSensitiveActionSettings(settings);
      setSettings({ ...defaults, ...(result || {}) });
      toast.success(t("Sensitive action settings saved"));
    } catch (error) { toast.error(error.message); }
    finally { setSaving(""); }
  };
  const saveCounterPin = async event => {
    event.preventDefault();
    try {
      setSaving("counter-pin");
      await advancedBillingApi.setCounterLockPin(pinUserId, counterPin);
      setCounterPin("");
      setUsers(current => current.map(user => String(user.userId) === String(pinUserId) ? { ...user, hasPin: true } : user));
      toast.success(t("Counter unlock PIN saved"));
    } catch (error) { toast.error(error.message); }
    finally { setSaving(""); }
  };
  const savePin = async event => {
    event.preventDefault();
    try {
      setSaving("pin");
      await advancedBillingApi.setManagerApprovalPin(pin);
      setPin("");
      toast.success(t("Manager approval PIN saved"));
    } catch (error) { toast.error(error.message); }
    finally { setSaving(""); }
  };

  if (loading) return <main className="security-settings-page"><PageLoader label={t("Loading security settings...")} /></main>;

  return <main className="security-settings-page">
    <header className="security-settings-heading">
      <span className="security-settings-icon"><ShieldCheck size={26} /></span>
      <div><p>{t("ACCESS CONTROL")}</p><h1>{t("Sensitive action security")}</h1>
        <span>{t("Choose when staff and cashiers need an Admin or Manager PIN. Role permissions and audit history always remain active.")}</span></div>
    </header>

    <form className="security-settings-card" onSubmit={saveSettings}>
      <label className="security-master-switch">
        <span><strong>{t("Require approval PIN for sensitive actions")}</strong>
          <small>{t("Turn this off to let authorized staff complete the actions below without a PIN.")}</small></span>
        <input type="checkbox" checked={settings.requireManagerPin}
          onChange={event => setOption("requireManagerPin", event.target.checked)} />
      </label>

      <div className={`security-action-list ${settings.requireManagerPin ? "" : "is-disabled"}`}>
        <label><span><strong>{t("Refund payment")}</strong><small>{t("Approve full or item-wise refunds.")}</small></span>
          <input type="checkbox" disabled={!settings.requireManagerPin} checked={settings.requirePinForRefund}
            onChange={event => setOption("requirePinForRefund", event.target.checked)} /></label>
        <label><span><strong>{t("Void bill items")}</strong><small>{t("Approve removing items from an unpaid bill.")}</small></span>
          <input type="checkbox" disabled={!settings.requireManagerPin} checked={settings.requirePinForVoid}
            onChange={event => setOption("requirePinForVoid", event.target.checked)} /></label>
        <label><span><strong>{t("Payment modification")}</strong><small>{t("Approve corrections to payment methods on a paid bill.")}</small></span>
          <input type="checkbox" disabled={!settings.requireManagerPin} checked={settings.requirePinForPaymentCorrection}
            onChange={event => setOption("requirePinForPaymentCorrection", event.target.checked)} /></label>
      </div>
      <button className="security-primary-button" disabled={Boolean(saving)} aria-busy={saving === "settings"}>
        {saving === "settings" ? <ButtonLoader label={t("Saving...")} /> : t("Save security settings")}
      </button>
    </form>

    <form className="security-settings-card" onSubmit={saveSettings}>
      <label className="security-master-switch">
        <span><strong>{t("Lock shared counter when unattended")}</strong>
          <small>{t("Keep the signed-in session and current page, but require an employee PIN after inactivity.")}</small></span>
        <input type="checkbox" checked={settings.enableCounterScreenLock}
          onChange={event => setOption("enableCounterScreenLock", event.target.checked)} />
      </label>
      <label className={`security-timeout-field ${settings.enableCounterScreenLock ? "" : "is-disabled"}`}>
        <span><strong>{t("Lock after")}</strong><small>{t("Choose 1 to 120 minutes of no keyboard, mouse or touch activity.")}</small></span>
        <span className="security-timeout-input"><input type="number" min="1" max="120" disabled={!settings.enableCounterScreenLock}
          value={settings.counterScreenLockMinutes}
          onChange={event => setOption("counterScreenLockMinutes", Number(event.target.value))} /><b>{t("minutes")}</b></span>
      </label>
      <button className="security-primary-button" disabled={Boolean(saving)} aria-busy={saving === "settings"}>
        {saving === "settings" ? <ButtonLoader label={t("Saving...")} /> : t("Save counter lock settings")}
      </button>
    </form>

    <form className="security-settings-card security-pin-card" onSubmit={saveCounterPin}>
      <div><h2>{t("Employee counter PINs")}</h2>
        <p>{t("Admin can reset any PIN. Managers can set their own and staff PINs, but cannot reset an Admin PIN.")}</p></div>
      <div className="security-user-pin-row">
        <label className="security-user-select"><span>{t("Employee")}</span>
          <select required value={pinUserId} onChange={event => setPinUserId(event.target.value)}>
            {manageableUsers.map(user => <option key={user.userId} value={user.userId}>
              {user.name} · {roleMap[user.roleId] || t("User")} {user.hasPin ? `· ${t("PIN set")}` : `· ${t("No PIN")}`}
            </option>)}
          </select>
        </label>
        <PasswordInput required inputMode="numeric" minLength="4" maxLength="8" pattern="[0-9]+"
          autoComplete="new-password" toggleLabel={t("counter PIN")} placeholder={t("New counter PIN")}
          value={counterPin} onChange={event => setCounterPin(event.target.value)} />
        <button className="security-primary-button" disabled={Boolean(saving) || !pinUserId} aria-busy={saving === "counter-pin"}>
          {saving === "counter-pin" ? <ButtonLoader label={t("Saving...")} /> : t("Set employee PIN")}
        </button>
      </div>
    </form>

    <form className="security-settings-card security-pin-card" onSubmit={savePin}>
      <div><h2>{t("Manager approval PIN")}</h2>
        <p>{t("Set your own 4–8 digit PIN. Staff and cashiers can use any active Admin or Manager PIN when approval is enabled.")}</p></div>
      <div className="security-pin-row">
        <PasswordInput required inputMode="numeric" minLength="4" maxLength="8" pattern="[0-9]+"
          autoComplete="new-password" toggleLabel={t("approval PIN")} placeholder={t("New approval PIN")}
          value={pin} onChange={event => setPin(event.target.value)} />
        <button className="security-primary-button" disabled={Boolean(saving)} aria-busy={saving === "pin"}>
          {saving === "pin" ? <ButtonLoader label={t("Saving...")} /> : t("Save PIN")}
        </button>
      </div>
    </form>
  </main>;
};

export default SecuritySettings;


